import { CodeusAccountConfig } from 'src/types/dataTypes';
import { createAsyncThunk } from '@reduxjs/toolkit';

type EmailSettingsReturnType = {
	sendDigests: boolean;
	sendEndUserAlerts: boolean;
	sendRemediationAlerts: boolean;
	alertsBatchingHourlyInterval: number;
	showSenders: boolean;
	emailDestinations: string;
};

type GetAccountConfigPayload = {
	api: {
		getAccountConfig: (id: string) => Promise<CodeusAccountConfig>;
	};
	id: string;
};

type SaveAccountConfigPayload = {
	settings: EmailSettingsReturnType;
	api: {
		saveAccountConfig: (id: string, params: any) => Promise<CodeusAccountConfig>;
	};
	id: string;
};

// This thunk fetches the email settings from the server
export const fetchEmailSettings = createAsyncThunk<
	EmailSettingsReturnType,
	GetAccountConfigPayload,
	{ rejectValue: string }
>('emailSettings/fetch', async ({ api, id }, { rejectWithValue }) => {
	try {
		const response = await api.getAccountConfig(id);
		return {
			sendDigests: response.digestConfiguration?.sendDigests || false,
			sendEndUserAlerts:
				response.alertConfiguration?.sendEndUserAlerts || false,
			sendRemediationAlerts:
				response.alertConfiguration?.sendRemediationAlerts || false,
			alertsBatchingHourlyInterval:
				response.alertConfiguration?.alertsBatchingHourlyInterval || 0,
			showSenders:
				!!response.digestConfiguration?.emailDestinations?.length,
			emailDestinations: response.digestConfiguration?.emailDestinations
				? response.digestConfiguration.emailDestinations.join(', ')
				: '',
		};
	} catch (error) {
		console.error('Fetch failed:', error);
		return rejectWithValue('Failed to fetch email settings');
	}
});

// This thunk updates the email settings on the server
export const updateEmailSettings = createAsyncThunk<
	EmailSettingsReturnType,
	SaveAccountConfigPayload,
	{ rejectValue: string }
>(
	'emailSettings/update',
	async ({ settings, api, id }, { rejectWithValue }) => {
		try {
			const patchObj = [
				{
					path: '/digestConfiguration/sendDigests',
					op: 'replace',
					value: settings.sendDigests,
				},
				{
					path: '/alertConfiguration/sendEndUserAlerts',
					op: 'replace',
					value: settings.sendEndUserAlerts,
				},
				{
					path: '/alertConfiguration/sendRemediationAlerts',
					op: 'replace',
					value: settings.sendRemediationAlerts,
				},
				{
					path: '/alertConfiguration/alertsBatchingHourlyInterval',
					op: 'replace',
					value: settings.alertsBatchingHourlyInterval,
				},
				{
					path: '/digestConfiguration/emailDestinations',
					op: 'replace',
					value:
						settings.showSenders && settings.emailDestinations
							? settings.emailDestinations
									.trim()
									.split(',')
									.map((s) => s.trim())
							: [],
				},
			];

			await api.saveAccountConfig(id, patchObj);
			return settings;
		} catch (error) {
			console.error('Update failed:', error);
			return rejectWithValue('Failed to update email settings');
		}
	},
);
